import {
  manufacturerOfEstimate,
  orderWarrantyAdvice,
  registeredTierOfEstimate,
  roofSquares,
  type JobFileEntry,
  type OrderWarrantyAdvice,
  type RegisteredTier,
  type WarrantyLine,
  type WarrantyManufacturer,
} from "@roof-hawk/core";

import {
  fetchRoofLayouts,
  insertRow,
  registerJobFile,
  updateRow,
  uploadJobFile,
} from "./queries";
import { supabase, unwrap } from "./supabase";

/**
 * Reads and writes for manufacturer warranties (ADR 0309).
 *
 * Its own file rather than more of `queries.ts`: the Warranties page reads
 * four tables that nothing else reads together, and the form that registers
 * one writes a row, a receipt and nothing else.
 */

/** The job a warranty belongs to, as the Warranties table lists it. */
export interface WarrantyJob {
  id: string;
  job_number: number;
  address: string | null;
  city: string | null;
  state: string | null;
  zip: string | null;
  stage?: string | null;
  contact: {
    full_name: string | null;
    address?: string | null;
    city?: string | null;
    state?: string | null;
    zip?: string | null;
  } | null;
}

export interface WarrantyRow {
  id: string;
  job_id: string;
  estimate_id: string | null;
  job_expense_id: string | null;
  status: "registered" | "pending" | "ineligible";
  ineligible_reason: string | null;
  manufacturer: WarrantyManufacturer | null;
  tier: RegisteredTier | null;
  warranty_number: string | null;
  order_number: string | null;
  receipt_number: string | null;
  shingle_product: string | null;
  shingle_color: string | null;
  squares: number | string | null;
  accessories: string[];
  fee: number | string;
  installed_on: string | null;
  registered_on: string | null;
  note: string | null;
  created_at: string;
  job: WarrantyJob | null;
}

export const JOB_COLUMNS = `id,job_number,address,city,state,zip,stage,contact:contacts!jobs_contact_id_fkey(full_name,address,city,state,zip)`;
const WARRANTY_COLUMNS = `*, job:jobs!job_warranties_job_id_fkey(${JOB_COLUMNS})`;

/** Every warranty row, newest registration first. */
export async function fetchWarranties(): Promise<WarrantyRow[]> {
  return unwrap<WarrantyRow[]>(
    await supabase
      .from("job_warranties")
      .select(WARRANTY_COLUMNS)
      .order("registered_on", { ascending: false, nullsFirst: false })
      .order("created_at", { ascending: false }),
  );
}

/** The one warranty row a job has, or null. */
export async function fetchJobWarranty(
  jobId: string,
): Promise<WarrantyRow | null> {
  const rows = unwrap<WarrantyRow[]>(
    await supabase
      .from("job_warranties")
      .select(WARRANTY_COLUMNS)
      .eq("job_id", jobId)
      .order("created_at", { ascending: false })
      .limit(1),
  );
  return rows[0] ?? null;
}

/** What the form writes. Dates are ISO days; squares and fee are decimals. */
export interface WarrantyValues {
  status: "registered" | "pending" | "ineligible";
  ineligible_reason: string | null;
  manufacturer: WarrantyManufacturer | null;
  tier: RegisteredTier | null;
  warranty_number: string | null;
  order_number: string | null;
  receipt_number: string | null;
  shingle_product: string | null;
  shingle_color: string | null;
  squares: number | null;
  accessories: string[];
  fee: number;
  installed_on: string | null;
  registered_on: string | null;
  note: string | null;
}

/** Write a job's warranty: a new row, or the row it already has. */
export async function saveWarranty(entry: {
  jobId: string;
  estimateId: string | null;
  existingId: string | null;
  values: WarrantyValues;
  profileId: string | null;
}): Promise<string> {
  if (entry.existingId) {
    await updateRow("job_warranties", entry.existingId, { ...entry.values });
    return entry.existingId;
  }
  const row = await insertRow<{ id: string }>("job_warranties", {
    job_id: entry.jobId,
    estimate_id: entry.estimateId,
    created_by: entry.profileId,
    ...entry.values,
  });
  return row.id;
}

/**
 * File the manufacturer's receipt on the warranty and on the job's Docs card.
 *
 * One `job_files` row, read two ways: Docs lists it by job, the Warranties
 * table by `source_table`/`source_id`. A second receipt on the same warranty
 * replaces the first, which is the upsert's doing and what a corrected
 * registration wants.
 */
export async function uploadWarrantyReceipt(entry: {
  jobId: string;
  warrantyId: string;
  file: File;
  profileId: string | null;
  title: string;
}): Promise<void> {
  const path = await uploadJobFile("job-files", entry.jobId, entry.file);
  await registerJobFile({
    jobId: entry.jobId,
    kind: "warranty_receipt",
    title: entry.title,
    bucket: "job-files",
    path,
    mimeType: entry.file.type || null,
    sizeBytes: entry.file.size,
    sourceTable: "job_warranties",
    sourceId: entry.warrantyId,
    uploadedBy: entry.profileId,
  });
}

/**
 * Each warranty's receipt, by warranty id.
 *
 * Two places to look: a receipt uploaded on the form carries the warranty as
 * its source; one filed on the other expense before the table existed
 * carries the expense (ADR 0297). Newest wins when both are there.
 */
export async function fetchWarrantyReceipts(
  rows: readonly Pick<WarrantyRow, "id" | "job_expense_id">[],
): Promise<Map<string, JobFileEntry>> {
  const out = new Map<string, JobFileEntry>();
  if (rows.length === 0) return out;
  const ids = rows.map((r) => r.id);
  const expenseIds = rows
    .map((r) => r.job_expense_id)
    .filter((id): id is string => Boolean(id));
  type FileRow = JobFileEntry & {
    source_table: string | null;
    source_id: string | null;
    job_expense_id: string | null;
  };
  const [bySource, byExpense] = await Promise.all([
    unwrap<FileRow[]>(
      await supabase
        .from("job_files")
        .select("*")
        .eq("source_table", "job_warranties")
        .in("source_id", ids)
        .order("created_at", { ascending: false }),
    ),
    expenseIds.length === 0
      ? Promise.resolve([] as FileRow[])
      : unwrap<FileRow[]>(
          await supabase
            .from("job_files")
            .select("*")
            .in("job_expense_id", expenseIds)
            .order("created_at", { ascending: false }),
        ),
  ]);
  for (const file of byExpense) {
    const row = rows.find((r) => r.job_expense_id === file.job_expense_id);
    if (row && !out.has(row.id)) out.set(row.id, file);
  }
  for (const file of bySource) {
    if (file.source_id && !out.has(file.source_id))
      out.set(file.source_id, file);
  }
  return out;
}

/** What the form can fill in before anyone types. */
export interface RegisterFacts {
  estimateId: string | null;
  /** From the active estimate's own tier. */
  manufacturer: WarrantyManufacturer | null;
  tier: RegisteredTier | null;
  shingleProduct: string | null;
  shingleColor: string | null;
  /** The roof's squares, without waste: what the manufacturer registers. */
  squares: number | null;
  /** The day the work was marked complete. */
  installedOn: string | null;
  /** What the material order says the roof qualifies for. */
  advice: OrderWarrantyAdvice | null;
}

export interface OrderLines {
  id: string;
  job_id: string;
  status: string | null;
  created_at: string;
  items: (WarrantyLine & { color?: string | null })[];
}

/** The job's material orders, latest first, each with its lines. */
async function fetchJobOrders(jobId: string): Promise<OrderLines[]> {
  return unwrap<OrderLines[]>(
    await supabase
      .from("material_orders")
      .select(
        "id,job_id,status,created_at,items:material_order_items(id,product_id,name,color,qty,unit,unit_price)",
      )
      .eq("job_id", jobId)
      .eq("is_live", true)
      .order("created_at", { ascending: false }),
  );
}

/** The catalogue's category for each product, which the names alone lack. */
export async function fetchProductParts(): Promise<
  Map<string, { category: string | null }>
> {
  const rows = unwrap<{ id: string; category: string | null }[]>(
    await supabase.from("products").select("id,category"),
  );
  return new Map(rows.map((r) => [r.id, { category: r.category }]));
}

export function withCategories(
  items: readonly (WarrantyLine & { color?: string | null })[],
  parts: ReadonlyMap<string, { category: string | null }>,
): (WarrantyLine & { color?: string | null })[] {
  return items.map((item) => ({
    ...item,
    category:
      item.category ??
      (item.product_id ? (parts.get(item.product_id)?.category ?? null) : null),
  }));
}

interface ActiveEstimate {
  id: string;
  warranty: string | null;
}

/** The active estimate's id and tier, or null when the job has none. */
async function fetchActiveEstimate(
  jobId: string,
): Promise<ActiveEstimate | null> {
  const rows = unwrap<ActiveEstimate[]>(
    await supabase
      .from("estimates")
      .select("id,warranty")
      .eq("job_id", jobId)
      .eq("is_active", true)
      .limit(1),
  );
  return rows[0] ?? null;
}

/** The day the job's work was marked complete, or null. */
async function fetchWorkCompleteDay(jobId: string): Promise<string | null> {
  const rows = unwrap<{ completed_at: string | null }[]>(
    await supabase
      .from("job_checklist_items")
      .select("completed_at")
      .eq("job_id", jobId)
      .eq("label", "All Work Complete")
      .eq("status", "done")
      .limit(1),
  );
  return rows[0]?.completed_at?.slice(0, 10) ?? null;
}

/** The shingle line on an order: the one that names a manufacturer. */
function shingleLine<L extends WarrantyLine>(lines: readonly L[]): L | null {
  return (
    lines.find((line) => orderWarrantyAdvice([line])?.manufacturer != null) ??
    null
  );
}

export async function fetchRegisterFacts(
  jobId: string,
): Promise<RegisterFacts> {
  const [estimate, orders, layouts, installedOn, parts] = await Promise.all([
    fetchActiveEstimate(jobId),
    fetchJobOrders(jobId),
    fetchRoofLayouts(jobId),
    fetchWorkCompleteDay(jobId),
    fetchProductParts(),
  ]);
  const lines = withCategories(orders[0]?.items ?? [], parts);
  const advice = orderWarrantyAdvice(lines);
  const shingle = shingleLine(lines);
  const squares = roofSquares(layouts);
  return {
    estimateId: estimate?.id ?? null,
    ...tierFacts(estimate?.warranty ?? null, advice),
    shingleProduct: shingle?.name ?? null,
    shingleColor: shingle?.color ?? null,
    squares: squares > 0 ? Math.round(squares * 100) / 100 : null,
    installedOn,
    advice,
  };
}

/**
 * Whose warranty, and which: the estimate names the manufacturer, and the
 * order's parts say what it qualifies for; either stands in for the other.
 */
function tierFacts(
  warranty: string | null,
  advice: OrderWarrantyAdvice | null,
): Pick<RegisterFacts, "manufacturer" | "tier"> {
  return {
    manufacturer:
      manufacturerOfEstimate(warranty) ?? advice?.manufacturer ?? null,
    tier: advice?.tier ?? registeredTierOfEstimate(warranty),
  };
}
